import { Fragment, useEffect, useMemo, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import Select from "react-select";
import userPic from "../../asset/image/default.png";
import { ReactComponent as DeleteIconDark } from "../../asset/icons/deleteIconDark.svg";
import { ReactComponent as EditIonDark } from "../../asset/icons/editIconDark.svg";
import { del, get, post, put } from "../../controlers/global/ConfigRequest";
import {
    COMPANY_ROLE,
    GET_COMPANY_ROLES_LIST,
    GET_USER_OF_COMPANY,
} from "../../helperData/ApiAddress";
import ConfirmModal from "./ConfirmModal";
import TreeSearchSelectByParentID from "../../component/global/TreeSearchSelectByParentID";
import { useTranslation } from "react-i18next";

function toUserOption(item) {
    const name = `${item.first_name || ""} ${item.last_name || ""}`.trim();
    const phone = item.phone_number || "";
    const labelParts = [name || null, phone || null].filter(Boolean);
    return {
        value: item.id,
        label: labelParts.join(" — "),
        user: item,
    };
}

export default function OrgPost({ form, auth }) {
    const [postTitle, setPostTitle] = useState("");
    const [postTitleErr, setPostTitleErr] = useState("");
    const [highLevelErr, setHighLevelErr] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [userErr, setUserErr] = useState("");
    const [formData, setFormData] = useState([]);
    const [btnLoader, setBtnLoader] = useState(false);
    const [listLoader, setListLoader] = useState(false);
    const [modalShow, setModalShow] = useState(false);
    const { t } = useTranslation();

    const [companyRulesTree, setCompanyRulesTree] = useState([]);
    const [memberOptions, setMemberOptions] = useState([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [
        itemTypeTreeSelectedHigherRules,
        setItemTypeTreeSelectedHigherRules,
    ] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [company, setCompany] = useState("");

    const companyId = form?.id || auth?.usercompany;

    const selectStyles = useMemo(
        () => ({
            control: (base, state) => ({
                ...base,
                minHeight: 40,
                borderRadius: 8,
                borderColor: userErr
                    ? "#e53e3e"
                    : state.isFocused
                      ? "#2E71FC"
                      : "rgba(0,0,0,0.12)",
                boxShadow: state.isFocused
                    ? "0 0 0 2px rgba(46,113,252,0.2)"
                    : "none",
                "&:hover": {
                    borderColor: userErr ? "#e53e3e" : "#2E71FC",
                },
            }),
            menu: (base) => ({
                ...base,
                zIndex: 30,
                borderRadius: 8,
            }),
            option: (base, state) => ({
                ...base,
                fontSize: 13,
                backgroundColor: state.isSelected
                    ? "#2E71FC"
                    : state.isFocused
                      ? "rgba(46,113,252,0.08)"
                      : "transparent",
            }),
        }),
        [userErr]
    );

    const getCompanyRules = () => {
        if (!companyId) return;
        setCompanyRulesTree([]);
        get(GET_COMPANY_ROLES_LIST.replace("{id}/", companyId)).then((r) => {
            setCompanyRulesTree(r);
        });
    };

    const getCompanyRoles = () => {
        setListLoader(true);
        get(COMPANY_ROLE)
            .then((r) => {
                setFormData(r.results || []);
            })
            .finally(() => setListLoader(false));
    };

    const loadMembers = (query = "") => {
        if (!companyId) return;
        setMembersLoading(true);
        get(GET_USER_OF_COMPANY.replace("{id}/", companyId), {
            search: query || undefined,
        })
            .then((r) => {
                const list = Array.isArray(r) ? r : r?.results || [];
                setMemberOptions(list.map(toUserOption));
            })
            .catch(() => setMemberOptions([]))
            .finally(() => setMembersLoading(false));
    };

    useEffect(() => {
        getCompanyRoles();
        getCompanyRules();
        loadMembers();
    }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

    const resetForm = () => {
        setItemTypeTreeSelectedHigherRules(null);
        setEditMode(false);
        setSelectedUser(null);
        setPostTitle("");
        setHighLevelErr("");
        setUserErr("");
        setPostTitleErr("");
        setCompany(form?.name || "");
    };

    useEffect(() => {
        resetForm();
    }, [form?.name]); // eslint-disable-line react-hooks/exhaustive-deps

    const addCompanyRole = () => {
        setHighLevelErr("");
        setUserErr("");
        setPostTitleErr("");

        let hasError = false;
        if (postTitle === "") {
            setPostTitleErr(t("Src.Pages.Profile.OrgPost.setPostTitleErr"));
            hasError = true;
        }
        if (!selectedUser?.value) {
            setUserErr(t("Organizational.Profile.OrgUsers.Select.Required"));
            hasError = true;
        }
        if (hasError) return false;

        setBtnLoader(true);
        const payload = {
            company: companyId,
            name: postTitle,
            user_id: selectedUser.value,
            parent: itemTypeTreeSelectedHigherRules,
        };

        const request = editMode
            ? put(COMPANY_ROLE + editMode + "/", payload)
            : post(COMPANY_ROLE, payload);

        request
            .then(() => {
                getCompanyRoles();
                resetForm();
                getCompanyRules();
            })
            .catch((e) => {
                const data = e?.response?.data || {};
                if (Object.keys(data).includes("parent")) {
                    setHighLevelErr(
                        t("Src.Pages.Profile.OrgPost.CompsetHighLevelErr")
                    );
                }
                if (
                    Object.keys(data).includes("user_id") ||
                    Object.keys(data).includes("phone_number")
                ) {
                    setUserErr(
                        Array.isArray(data.user_id)
                            ? data.user_id[0]
                            : t("Src.Pages.Profile.OrgPost.setUserErr")
                    );
                }
                if (Object.keys(data).includes("detail")) {
                    setUserErr(t("Src.Pages.Profile.OrgPost.CompsetUserErr"));
                }
            })
            .finally(() => setBtnLoader(false));
    };

    const deleteCompanyRole = (id) => {
        del(COMPANY_ROLE + id).then(() => {
            getCompanyRoles();
            getCompanyRules();
        });
    };

    const editCompanyRole = (id) => {
        const data = formData.filter((item) => item.id === id)[0];
        if (!data) return;
        setPostTitle(data.name);
        setEditMode(id);
        setItemTypeTreeSelectedHigherRules(data.parent);
        if (data.user) {
            setSelectedUser(toUserOption(data.user));
        }
        window.scrollTo(0, 0);
    };

    return (
        <div className="page-load-fade">
            <form className="general-info-form" autoComplete="off">
                <Form.Group controlId="orgtitle">
                    <Form.Label>
                        {t("Organizational.Profile.PositionTitle.Label")}
                    </Form.Label>
                    <div>
                        <Form.Control
                            type="text"
                            name="position_title"
                            autoComplete="off"
                            value={postTitle}
                            onChange={(e) => setPostTitle(e.target.value)}
                        />
                        {postTitleErr ? (
                            <div className="err">{postTitleErr}</div>
                        ) : null}
                    </div>
                </Form.Group>
                <Form.Group controlId="highlevel">
                    <Form.Label>
                        {t("Organizational.Profile.UpperPosition.Label")}
                    </Form.Label>
                    <div>
                        <TreeSearchSelectByParentID
                            value={itemTypeTreeSelectedHigherRules}
                            data={companyRulesTree}
                            onChange={(e) =>
                                setItemTypeTreeSelectedHigherRules(e)
                            }
                        />
                        {highLevelErr ? (
                            <div className="err">{highLevelErr}</div>
                        ) : null}
                    </div>
                </Form.Group>
                <Form.Group controlId="org">
                    <Form.Label>
                        {t(
                            "Organizational.Profile.Organizational.Place.Holder"
                        )}
                    </Form.Label>
                    <Form.Control
                        type="text"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        readOnly
                    />
                </Form.Group>
                <Form.Group controlId="org-post-user">
                    <Form.Label>
                        {t("Organizational.Profile.User.Place.Holder")}
                    </Form.Label>
                    <Select
                        inputId="org-post-user"
                        classNamePrefix="org-post-user-select"
                        placeholder={t(
                            "Organizational.Profile.OrgUsers.Select.Placeholder"
                        )}
                        isClearable
                        isLoading={membersLoading}
                        options={memberOptions}
                        value={selectedUser}
                        onChange={setSelectedUser}
                        onInputChange={(value, meta) => {
                            if (meta.action === "input-change") {
                                loadMembers(value);
                            }
                            if (meta.action === "menu-close") {
                                loadMembers();
                            }
                        }}
                        noOptionsMessage={() =>
                            t("Organizational.Profile.OrgUsers.Select.Empty")
                        }
                        filterOption={() => true}
                        styles={selectStyles}
                        aria-label={t(
                            "Organizational.Profile.User.Place.Holder"
                        )}
                    />
                    {userErr ? <div className="err">{userErr}</div> : null}
                </Form.Group>
            </form>

            <div className="general-info-form">
                <div className="btn-group">
                    <button
                        type="button"
                        className="blue"
                        onClick={addCompanyRole}
                    >
                        {btnLoader ? (
                            <Spinner animation="border" />
                        ) : editMode ? (
                            <span>
                                {t(
                                    "Organizational.Profile.MakeUser.Edit.Button"
                                )}
                            </span>
                        ) : (
                            <span>
                                {t("Organizational.Profile.MakeUser.Button")}
                            </span>
                        )}
                    </button>
                    {editMode ? (
                        <button
                            type="button"
                            className="red"
                            onClick={resetForm}
                        >
                            {t("Organizational.Profile.Cancel.Button")}
                        </button>
                    ) : null}
                </div>
            </div>
            <div className="table-container org-post-table-container">
                <table className="user-table">
                    <thead>
                        <tr className="header-row">
                            <th>{t("Organizational.Profile.Row.Label")}</th>
                            <th>{t("Organizational.Profile.User.Label")}</th>
                            <th>
                                {t(
                                    "Organizational.Profile.PositionTitle.Label"
                                )}
                            </th>
                            <th>
                                {t(
                                    "Organizational.Profile.UpperPosition.Label"
                                )}
                            </th>
                            <th>
                                {t("Organizational.Profile.Function.Label")}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {listLoader ? (
                            <tr>
                                <td colSpan="5">
                                    <Spinner animation="border" />
                                </td>
                            </tr>
                        ) : formData.length === 0 ? (
                            <tr>
                                <td colSpan="5">
                                    {t(
                                        "Organizational.Profile.ListIsEmpty.Label"
                                    )}
                                </td>
                            </tr>
                        ) : (
                            formData.map((item, index) => (
                                <Fragment key={item.id || index}>
                                    <tr className="user-table-row">
                                        <td>
                                            <div className="level">
                                                {index + 1}
                                            </div>
                                        </td>
                                        <td className="user">
                                            <div>
                                                <img
                                                    src={userPic}
                                                    alt=""
                                                    width={32}
                                                    height={32}
                                                />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="truncate">
                                                    {item.user?.first_name ||
                                                    item.user?.last_name
                                                        ? `${item.user.first_name || ""} ${item.user.last_name || ""}`.trim()
                                                        : item.user
                                                              ?.phone_number}
                                                </div>
                                                {item.user?.phone_number ? (
                                                    <div
                                                        className="org-post-user-phone tabular-nums"
                                                        dir="ltr"
                                                        translate="no"
                                                    >
                                                        {item.user.phone_number}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </td>
                                        <td>{item.name}</td>
                                        <td>{item.parent_name || "ندارد"}</td>
                                        <td className="edit-btns">
                                            <button
                                                type="button"
                                                aria-label={t(
                                                    "Organizational.Profile.MakeUser.Remove.User.Button"
                                                )}
                                                onClick={() =>
                                                    setModalShow(index)
                                                }
                                            >
                                                <DeleteIconDark aria-hidden="true" />
                                            </button>
                                            <button
                                                type="button"
                                                aria-label={t(
                                                    "Organizational.Profile.MakeUser.Edit.Button"
                                                )}
                                                onClick={() =>
                                                    editCompanyRole(item.id)
                                                }
                                            >
                                                <EditIonDark aria-hidden="true" />
                                            </button>
                                        </td>
                                    </tr>
                                    <ConfirmModal
                                        show={modalShow === index}
                                        onHide={() => setModalShow(false)}
                                        onConfirm={() => {
                                            deleteCompanyRole(item.id);
                                            setModalShow(false);
                                        }}
                                        title={t(
                                            "Organizational.Profile.MakeUser.Remove.User.Label"
                                        )}
                                        description={`${t(
                                            "Organizational.Profile.MakeUser.Remove.User.Desc.partOne"
                                        )} ${
                                            `${item.user?.first_name || ""} ${item.user?.last_name || ""}`.trim()
                                        } ${t(
                                            "Organizational.Profile.MakeUser.Remove.User.Desc.partTwo"
                                        )}`}
                                        btnTitle={t(
                                            "Organizational.Profile.MakeUser.Remove.User.Button"
                                        )}
                                    />
                                </Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
